import React from 'react'
import { Button } from './Button'
import styles from './Pagination.module.scss'

export interface PaginationInfo {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

export interface PaginationProps {
  pagination: PaginationInfo
  onPageChange: (page: number) => void
  itemName?: string // e.g., "competitions", "matches", "items"
  compact?: boolean // Use compact styling for tighter layouts
  className?: string
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  itemName = 'items',
  compact = false,
  className = ''
}) => {
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = []
    const { page, total_pages } = pagination
    
    // Show up to 5 page numbers
    const startPage = Math.max(1, page - 2)
    const endPage = Math.min(total_pages, page + 2)
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    
    return pages
  }

  // Don't render if there's only one page
  if (pagination.total_pages <= 1) {
    return null
  }

  const startItem = ((pagination.page - 1) * pagination.limit) + 1
  const endItem = Math.min(pagination.page * pagination.limit, pagination.total_count)

  const containerClass = `${styles.pagination} ${compact ? styles.compact : ''} ${className}`

  return (
    <div className={containerClass}>
      <div className={styles.info}>
        Showing {startItem} to {endItem} of {pagination.total_count} {itemName}
      </div>

      <div className={styles.controls}>
        <Button
          variant="secondary"
          size="small"
          onClick={() => onPageChange(pagination.page - 1)}
          disabled={!pagination.has_prev}
        >
          Previous
        </Button>

        <div className={styles.pageNumbers}>
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={pageNum === pagination.page ? 'primary' : 'secondary'}
              size="small"
              onClick={() => onPageChange(pageNum)}
              className={styles.pageButton}
            >
              {pageNum}
            </Button>
          ))}
        </div>

        <Button
          variant="secondary"
          size="small"
          onClick={() => onPageChange(pagination.page + 1)}
          disabled={!pagination.has_next}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export default Pagination
